// -----------------------------------------------------------------------------
// Library
// -----------------------------------------------------------------------------
import React, { useEffect, useState } from "react";
import { Field, Form } from "react-final-form";
import { useRecoilValue } from "recoil";
import notification from "antd/lib/notification";
import { DeleteUserModal } from "components/Modals/DeleteUserModal";
// -----------------------------------------------------------------------------
// Components and helpers
// -----------------------------------------------------------------------------
import InputField from "components/UI/Form/InputField";
import Label from "components/UI/Form/label";
import { Button } from "components/Common/Button";
import { validateRequiredEncodedLength, validateEmail } from "helpers/Form/commonFormValidations";
import { userAtom } from "globalAtoms";
import { useSetRecoilState } from "recoil";
import { getUser } from "useInit";
import { useUpdateUserProfileMutation } from "store/api/api";
import { Modal, Typography, Row, Col, Flex } from "antd";
import EditProfile from "components/EditProfile";
import { DeleteOutlined, RightOutlined } from "@ant-design/icons";
import usePrimaryColor from "helpers/hooks/usePrimaryColor";
import { useNavigate } from "react-router";
import PhoneInput from "antd-phone-input";

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------
const AccountSettingsForm = () => {
	const user = useRecoilValue(userAtom);
	const setUser = useSetRecoilState(userAtom);
	const [updateUserTrigger, updateUserResponse] = useUpdateUserProfileMutation({});
	const [openEditProfile, setOpenEditProfile] = React.useState(false);
	const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
	const primaryColor = usePrimaryColor();
	const navigate = useNavigate();

	const handleFormSubmit = async (formObj) => {
		const { firstName, lastName } = formObj;
		await updateUserTrigger({
			user_id: user.id,
			first_name: firstName,
			last_name: lastName,
		});

		await getUser(setUser);
	};

	// Monitor for loading state
	useEffect(() => {
		if (updateUserResponse.isSuccess) {
			notification.success({
				message: "Updated user info!",
			});
		}
		if (updateUserResponse.isError) {
			if (updateUserResponse.error.status === 409) {
				notification.error({
					message: updateUserResponse.error?.data?.message,
				});
			} else {
				notification.error({
					message: "There was an error updating user info",
				});
			}
		}
	}, [updateUserResponse]);

	return (
		<>
			<DeleteUserModal open={showConfirmDeleteModal} setOpen={setShowConfirmDeleteModal} />
			<Form
				initialValues={{
					firstName: user?.first_name,
					lastName: user?.last_name,
					email: user?.email_address,
					phone: user?.phone_number,
				}}
				onSubmit={handleFormSubmit}
			>
				{({ handleSubmit }) => {
					return (
						<form onSubmit={handleSubmit}>
							<Row gutter={[16, 16]}>
								<Col xs={24} sm={12}>
									<Label>First Name</Label>
									<Field
										component="input"
										name="firstName"
										validate={(value) => validateRequiredEncodedLength(value, 40)}
									>
										{({ input, meta }) => <InputField data-id="txtFirstName" type="text" input={input} meta={meta} />}
									</Field>
								</Col>
								<Col xs={24} sm={12}>
									<Label>Last Name</Label>
									<Field
										component="input"
										name="lastName"
										validate={(value) => validateRequiredEncodedLength(value, 40)}
									>
										{({ input, meta }) => <InputField data-id="txtLastName" type="text" input={input} meta={meta} />}
									</Field>
								</Col>
							</Row>
							<Label>Phone Number</Label>
							<PhoneInput
								value={user?.phone_number}
								onClick={() => setOpenEditProfile(true)}
								data-id="btnChangeEmailPhone"
								onKeyDown={() => setOpenEditProfile(true)}
								disabled
							/>
							<Row style={{ marginTop: 16 }}>
								<Col span={24}>
									<Label>Email Address</Label>
									<Field component="input" name="email" validate={validateEmail}>
										{({ input, meta }) => (
											<InputField
												data-id="txtEmail"
												type="text"
												input={input}
												meta={meta}
												onClick={() => setOpenEditProfile(true)}
												onKeyDown={() => setOpenEditProfile(true)}
												disabled
											/>
										)}
									</Field>
								</Col>
							</Row>

							<Flex
								justify="space-between"
								gap={16}
								align="center"
								style={{ marginBottom: 16, cursor: "pointer", marginTop: 24 }}
								className="action-button"
								onClick={() => setShowConfirmDeleteModal(true)}
								data-id="btnDeleteAccount"
							>
								<DeleteOutlined />
								<Typography.Text style={{ flex: 1, color: primaryColor }}>Delete Account</Typography.Text>
								<RightOutlined style={{ color: "#000" }} />
							</Flex>

							<Button
								className="primary-outlined--delete"
								data-id="btnCancel"
								style={{
									flex: 1,
									whiteSpace: "nowrap",
									minWidth: "unset",
									maxWidth: "unset",
									marginTop: 80,
									marginBottom: 16,
								}}
								onClick={() => navigate(-1)}
							>
								Cancel
							</Button>
							<Button className="primary-filled" loading={updateUserResponse.isLoading} data-id="btnSave" type="submit">
								Save
							</Button>
						</form>
					);
				}}
			</Form>
			<Modal
				open={openEditProfile}
				footer={null}
				title={<Typography.Title level={3}>Edit Profile</Typography.Title>}
				onCancel={() => setOpenEditProfile(false)}
				destroyOnClose
				maskClosable={false}
			>
				<EditProfile onCancel={() => setOpenEditProfile(false)} />
			</Modal>
		</>
	);
};

export default AccountSettingsForm;

